import React, { useState, useEffect, useCallback } from "react";
import { Flex, Typography, Button, Tag } from "antd";
import { WarningFilled, ExclamationCircleFilled, CreditCardOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getPaymentHistory } from "../../../services/organizationService";

const { Text } = Typography;

const THRESHOLD_DAYS = 10;

const calculateDaysRemaining = (targetDate) => {
  if (!targetDate) return null;
  const target = new Date(targetDate).getTime();
  const now = Date.now();
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
};

const resolveSubscriptionDetails = (res) => {
  if (!res || !res.success) return null;

  const rawSubTo = res.subscriptionTo || res.payments?.[0]?.toDate;
  const status = (res.status || res.payments?.[0]?.status || "").toLowerCase();
  const trialStart = res.trialStartDate;

  let daysLeft = null;
  let expiryDate = null;
  let isTrial = false;

  if (rawSubTo) {
    expiryDate = new Date(rawSubTo);
    daysLeft = calculateDaysRemaining(rawSubTo);
  } else if (trialStart) {
    isTrial = true;
    const trialEnd = new Date(new Date(trialStart).getTime() + 7 * 24 * 60 * 60 * 1000);
    expiryDate = trialEnd;
    daysLeft = calculateDaysRemaining(trialEnd);
  }

  const isDue = status === "on due" || (daysLeft !== null && daysLeft <= 0);

  return {
    daysLeft: isDue ? 0 : daysLeft,
    isExpired: isDue,
    expiryDate,
    isTrial,
    status,
  };
};

function SubscriptionAlertBanner({ companyCode, onRenew }) {
  const [subInfo, setSubInfo] = useState(null);

  const fetchSubscription = useCallback(async () => {
    if (!companyCode) return;
    try {
      const res = await getPaymentHistory(companyCode, { limit: 1 });
      const details = resolveSubscriptionDetails(res);
      setSubInfo(details);
    } catch (err) {
      console.warn("[SubscriptionAlertBanner] Failed to check subscription expiry:", err);
    }
  }, [companyCode]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  if (!subInfo || subInfo.daysLeft === null || subInfo.daysLeft > THRESHOLD_DAYS) {
    return null;
  }

  const { daysLeft, isExpired, expiryDate, isTrial } = subInfo;
  const formattedExpiry = expiryDate ? dayjs(expiryDate).format("DD MMM, YYYY") : "soon";

  const isCritical = isExpired || daysLeft <= 3;
  const bg = isCritical ? "#fff2f0" : "#fffbe6";
  const border = isCritical ? "#ffccc7" : "#ffe58f";
  const textColor = isCritical ? "#cf1322" : "#b76e00";
  const btnBg = isCritical ? "#ff4d4f" : "#fa8c16";
  const IconComponent = isCritical ? ExclamationCircleFilled : WarningFilled;

  const messageText = isExpired
    ? `Your DealVoice ${isTrial ? "free trial" : "subscription"} has expired. Renew your plan now to prevent interruption of your team's access.`
    : `Subscription Reminder: Your DealVoice ${isTrial ? "trial" : "plan"} expires in ${daysLeft} ${daysLeft === 1 ? "day" : "days"} (on ${formattedExpiry}). Renew now to ensure uninterrupted service.`;

  return (
    <div
      style={{
        background: bg,
        borderBottom: `1px solid ${border}`,
        padding: "10px 24px",
        transition: "all 0.3s ease",
        zIndex: 90,
      }}
    >
      <Flex align="center" justify="space-between" wrap="wrap" gap={12}>
        <Flex align="center" gap={10} style={{ flex: 1, minWidth: 260 }}>
          <IconComponent style={{ fontSize: 18, color: textColor }} />
          <Text style={{ fontSize: 13.5, fontWeight: 600, color: textColor }}>
            {messageText}
          </Text>
          {!isExpired && (
            <Tag color={isCritical ? "error" : "warning"} style={{ fontWeight: 700, borderRadius: 10 }}>
              {daysLeft} {daysLeft === 1 ? "day" : "days"} left
            </Tag>
          )}
        </Flex>

        <Button
          type="primary"
          icon={<CreditCardOutlined />}
          onClick={onRenew}
          style={{
            background: btnBg,
            borderColor: btnBg,
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 8,
            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
          }}
        >
          {isExpired ? "Renew Now" : "Renew Subscription"}
          <RightOutlined style={{ fontSize: 10, marginLeft: 4 }} />
        </Button>
      </Flex>
    </div>
  );
}

export default SubscriptionAlertBanner;
